/**
 * The lifecycle four configuration screens share — Task U14 (Tier-3 live-tenant
 * readiness), plan decision KTD13.
 *
 * Collection policies (U15), alert rules (U16), productivity formulas (U17) and
 * payroll formulas (U18) already share **one backend lifecycle**: list, create,
 * patch, activate, with a version as the concurrency token. Nothing forced them to
 * share a front end, and three or four screens inventing the same lifecycle is how
 * four different answers to "what does a stale version look like" end up in one
 * product. The states nobody thinks about drift furthest: a skeleton on one screen,
 * a spinner on the next, and a configuration type with no versions rendered as an
 * empty table on the third.
 *
 * So the states are enumerated **here, once**, and no screen decides them:
 *
 *   `skeleton`  the initial read is in flight.
 *   `empty`     this configuration type has no versions yet — a `200` with none, or
 *               a read refusal the surface's `emptyOnRefusal` classifies as
 *               emptiness (written for U18's single-formula `404`; since roadmap
 *               E0-4 U18 reads a history that answers `200` with none, and no
 *               screen declares one). The create action belongs on this state,
 *               which is why the panel takes one.
 *   `retry`     the read failed, and the surface did not classify the refusal as
 *               emptiness. `ApiErrorNote` renders the **server's own sentence**
 *               where `API_CONTRACT.md` §5.1 marks it safe, never a client
 *               paraphrase, and offers `reload` only where a retry could help.
 *   `ready`     there are versions.
 *
 * `conflict` is not a fifth phase. A refused write leaves the versions on screen and
 * the draft intact, so it rides beside `ready` rather than replacing it — and so do
 * `writeError`, `pending` and `saved`, which the panel renders too. A screen that had
 * to write its own refusal note, busy state and saved confirmation would be writing
 * three more states four times over, which is the drift KTD13 exists to stop.
 *
 * ── The draft carries only what the person actually touched ─────────────────
 *
 * `GeoPolicyPanel.tsx`'s pattern, generalised. `draft` is a `Partial<TDraft>`: a
 * field nobody touched is **absent**, so a patch body contains only what changed by
 * construction rather than by a diff computed at submit time. On a configuration
 * with twenty settings that is not a nicety — a body that resent every field would
 * carry nineteen values the person never looked at, and each one is a chance to
 * silently undo somebody else's concurrent edit.
 *
 * ── The four backends are not alike, which fix round 1 is mostly about ─────
 *
 * The first version of this file assumed they were, and said so: "every backend
 * U15–U18 targets sends `details.current_etag`". That claim was checked against the
 * policies module and against nothing else, and three of the four could not use the
 * hook because of it. What they actually do:
 *
 *   U15 `/policies`          `If-Match` mandatory; the ETag is `updated_at` at
 *                            microsecond precision, and it is **not on the list** —
 *                            among reads it is issued only on `GET /policies/{id}`
 *                            (`admin-routes.ts:291`), and the writes set it too
 *                            (`:281,308,323`). The list rows carry no `etag`, only a
 *                            millisecond `updated_at` the server rejects for having
 *                            three digits instead of six. A `409` carries
 *                            `details.current_etag`.
 *   U16 alert rules          `If-Match` mandatory, carrying the **version integer** —
 *                            and unlike U15 that integer **is** in the list rows
 *                            (`renderAlertRule`, `alerting/service.ts:223,230`,
 *                            used by the list at `routes.ts:274`). That contrast is
 *                            what makes U15's row a problem rather than a fact of
 *                            life: a list-shaped if-match surface can carry its
 *                            token, and one of these two does. A `409` carries
 *                            `{ expected, actual }` — no `current_etag` anywhere.
 *   U17 productivity formulas  **No concurrency control at all.** `PATCH
 *                            /productivity/formulas/{id}` and its activate read no
 *                            `If-Match`, take no body version and set no `ETag`.
 *   U18 payroll formulas     No `PATCH`. Until roadmap E0-4 it was create-is-activate
 *                            over a single-formula `GET`; since then the list is
 *                            `GET /payroll/formulas/versions`, a create is a draft,
 *                            and `POST /payroll/formulas/{version}/activate` carries a
 *                            typed confirmation in its body — the one activation with
 *                            a body, which is what `activation.body` is for.
 *
 * One thing the hook does **not** surface, and U15 will want it: a write's own
 * response `ETag`. `useApiMutation` exposes `etag` per mutation for exactly the case
 * of patching twice without re-reading, and `state.etag` here is the **read's**
 * only. Until a screen has a call site for it, adding it would be guessing at a
 * shape — the mistake fix round 1 was about.
 *
 * `preconditionOf` is the whole compare-and-set story in one function, and it now
 * has three answers rather than two. `{ kind: 'if-match' }` for U15 and U16;
 * `{ kind: 'body', field, value }` for a surface that carries the token in the body,
 * which the retention shape needs because it edits many rows at once and one request
 * header could not say which (`web/src/api/retention.ts`); and `{ kind: 'none' }`
 * for U17, which sends nothing and lets the last write win.
 *
 * `null` is not a fourth answer. It means **the token is missing**, and every write
 * on such a version is refused **here**: a write without a precondition the backend
 * will check is a blind overwrite. `row.writable` is `precondition !== null`, and
 * the panel renders no write control for a row that is not writable — the codebase's
 * absent-not-disabled rule, made structural rather than remembered four times.
 *
 * ── A refusal is never treated as a success, and never fires a request ──────
 *
 * The T2-H bug: `submit` resolves rather than rejects, so an unconditional
 * `.then()` runs on a refusal exactly as it runs on a success. Every side effect
 * here is instead keyed on the transition **to** `'ok'`, which a refusal never
 * reaches — so `activate` refetches once when it lands and not at all when it is
 * refused.
 *
 * That rule is stricter than `IncidentsScreen.tsx`, which re-reads after any
 * outcome on the stated reasoning that "a refusal means the state on screen is
 * stale". True, and still not worth an automatic request: the refusals a
 * configuration screen actually meets include `403` and `429`, and a screen that
 * answers a rate limit with another read is the wrong reflex to install in four
 * screens at once. More importantly the person has an unsaved draft on screen, and
 * moving the rows under them without a press is precisely what the conflict state
 * exists to avoid.
 *
 * So the stored value arrives one of two ways, both explicit:
 *   - `storedFromConflict`, when the surface's `409` body names it; or
 *   - `reload()` — one press, offered by the panel inside the conflict note. Until
 *     that happens `conflict.stored` is `null` and the note says so rather than
 *     printing the pre-conflict value under a "stored" label, which would be a lie.
 *
 * None of the four backends puts the stored **representation** in its `409` body —
 * U15 sends `current_etag`, U16 sends `{ expected, actual }`, U17 has no conflict to
 * report and U18 has no patch — so on all four the newer token is named immediately
 * and the stored value costs the press. The honest fix is server-side, and belongs to
 * whichever unit next touches those write paths.
 *
 * ── The second save is gated ────────────────────────────────────────────────
 *
 * U11's review round 1 found this on `RetentionScreen`: once the rows have been
 * read back, a refused draft's precondition is the **winner's**, so a second click
 * would carry a perfectly valid token and quietly overwrite the value that won.
 * `conflict.acknowledged` gates it, and a fresh conflict clears the acknowledgement
 * — it was minted against a specific stored value, and the refusal means that value
 * has moved.
 *
 * ── Creating a version that *is* the activation ─────────────────────────────
 *
 * A surface whose create immediately activates declares `activation: { kind: 'create' }`
 * and `activate` becomes an alias of `create` — one `POST` to `createPath` and nothing
 * else. No screen declares it: U18 was its only consumer, and since roadmap E0-4
 * payroll formulas are drafts a second person confirms. It stays, tested, because the
 * payroll backend still accepts the immediately-active create until roadmap E1.
 *
 * ── An activation with a body of its own ─────────────────────────────────────
 *
 * `{ kind: 'endpoint', body }` builds the activate request's body at press time —
 * roadmap E0-4's `{ confirmation }`, the confirmer's typed statement. It is a human
 * fact, not a concurrency token, so it does not ride in `preconditionOf`; a `body`
 * precondition is still merged on top of it. Omitted, the body is `{}`, as U15–U17 send.
 *
 * ── No screen-specific logic, and nothing imported from a screen ────────────
 *
 * Enforced, not promised: `useVersionedConfig.test.ts` fails if any file in this
 * directory imports from `web/src/screens/**`.
 */

import { useEffect, useRef, useState } from 'react';

import { useApiMutation } from '../api/useApiMutation';
import { useApiQuery, type QueryStatus } from '../api/useApiQuery';
import type { WorkforceApiError } from '../api/errors';

/** The `code` a compare-and-set refusal carries — `API_CONTRACT.md` §6.4. */
export const VERSION_CONFLICT_CODE = 'WORKFORCE_VERSION_CONFLICT';

/** The four states of this lifecycle, decided here so no screen decides them. */
export type VersionedConfigPhase = 'skeleton' | 'empty' | 'retry' | 'ready';

/** Which of the three writes a refusal belongs to. */
export type VersionedConfigAction = 'create' | 'patch' | 'activate';

/**
 * How one version's compare-and-set token travels, decided per version by the
 * surface.
 *
 * `null` from `preconditionOf` is **not** a fourth kind. It says "this version's
 * token is missing", and the hook refuses the write. `{ kind: 'none' }` says
 * something entirely different — "this backend runs no compare-and-set on this
 * write" — and the hook sends the request. Fix round 1 separated the two: they were
 * one value, and `PATCH /productivity/formulas/{id}` and its activate (U17) read no
 * `If-Match`, take no body version and set no `ETag`
 * (`src/modules/formulas/routes.ts:373,387`), so U17's surface could return nothing
 * but `null` and every write it made was silently swallowed by a guard meant for a
 * missing token.
 */
export type VersionedConfigPrecondition =
  /** `If-Match: "<token>"`. The value is the unquoted `ETag` a read carried. */
  | { readonly kind: 'if-match'; readonly token: string }
  /** A field in the request body — `{ version: 4 }` on the retention surface. */
  | { readonly kind: 'body'; readonly field: string; readonly value: string | number }
  /**
   * The backend runs no concurrency check on this write, so nothing is sent and last
   * write wins. U17's productivity formulas are this. Worth saying out loud on the
   * screen: someone editing a formula two people can edit deserves to know the
   * server will not stop the second save.
   */
  | { readonly kind: 'none' };

/** Where `activate` sends its request, or that there is no such request. */
export type VersionedConfigActivation<TVersion> =
  | {
      readonly kind: 'endpoint';
      readonly path: (version: TVersion) => string;
      /** Defaults to `POST` — `POST /policies/{id}/activate`. */
      readonly method?: 'POST' | 'PATCH';
      /**
       * The request body, built at press time. Omitted = `{}`. Roadmap E0-4: payroll's
       * `{ confirmation }` — see the module comment. A `body` precondition is merged over
       * whatever this returns.
       */
      readonly body?: (version: TVersion) => Readonly<Record<string, unknown>>;
    }
  /** Creating a version **is** the activation. `activate` becomes `create`. */
  | { readonly kind: 'create' };

/**
 * Everything this hook needs to know about one backend, and nothing about a screen.
 *
 * The functions are read through a ref rather than depended on, so a caller may
 * build this object inline on every render without re-firing the read.
 */
export interface VersionedConfigSurface<TList, TVersion, TDraft, TCreate> {
  /**
   * `GET` for the list. `null` means "there is nothing to ask yet" and parks the
   * hook in `skeleton` with no request in flight — `useApiQuery`'s own convention.
   */
  readonly listPath: string | null;
  /** The versions inside the read's envelope, in the order the server sent them. */
  readonly versions: (list: TList) => readonly TVersion[];
  /** Stable identity of one version — the row key, and what a refusal is matched on. */
  readonly idOf: (version: TVersion) => string;
  /** The version's name in words. The screen owns the wording, as everywhere else. */
  readonly labelOf: (version: TVersion) => string;
  /** Is this the version in force? Exactly one row wears the active marker. */
  readonly isActive: (version: TVersion) => boolean;
  /**
   * The comparable settings, already rendered, for the panel's diff.
   *
   * **Its keys must cover `keyof TDraft`.** The diff and the conflict comparison join
   * a draft key to a field key by string equality, and nothing types that
   * correspondence: a screen whose draft key is `retention_days` and whose field key
   * is `days` gets a silent "the version in force has no such field" rather than an
   * error. It is not typed `Record<keyof TDraft & string, string>` because `fieldsOf`
   * is also allowed to render settings that are not editable, which that type would
   * reject.
   */
  readonly fieldsOf: (version: TVersion) => Readonly<Record<string, string>>;
  /**
   * How this version's write carries its precondition, or `null` when the token is
   * missing and the write must be refused. `{ kind: 'none' }` for a backend that runs
   * no check at all — see `VersionedConfigPrecondition`.
   *
   * **A list-shaped `if-match` surface needs a per-row token in the body.** This is a
   * function of one list row, so a backend whose token lives only in a header cannot
   * be driven from a list at all. `/policies` was that shape and no longer is: U15
   * added `etag` to `renderPolicy`'s rows, and it is the same string the header
   * carries rather than a second rendering of it, because `ADMIN_POLICY_COLUMNS`
   * already computed `to_char(updated_at …'US"Z"')` on all five statements and it
   * simply never reached the wire. Do not reach for `updated_at` instead: `GET
   * /policies` renders it at millisecond precision and `ifMatchToken`
   * (`admin-routes.ts:196-203`) rejects three digits where it wants six. U16 never
   * had the problem — its list rows already carry the version its `If-Match` wants.
   * For a single-resource surface the read's own header is on the state as `etag`; a
   * **write's** response ETag is not surfaced at all, which costs a round trip rather
   * than correctness.
   */
  readonly preconditionOf: (version: TVersion) => VersionedConfigPrecondition | null;
  /** `POST` for a new version. */
  readonly createPath: string;
  /** The touched draft turned into a create body — required fields, defaults. */
  readonly createBody: (draft: Partial<TDraft>) => TCreate;
  /**
   * `PATCH` for one version, or `null` on a surface whose versions are immutable.
   *
   * `activation: { kind: 'create' }` does **not** imply this is `null`, and the hook
   * does not couple them: a backend could plausibly let a draft be edited and then
   * activate by creating. U18's `/payroll/formulas` exposes no `PATCH` at all, so its
   * surface sets `null`, and the guarantee its review will check — "an in-use
   * version's edit control is absent, not disabled" — lives in that surface.
   */
  readonly patchPath: ((version: TVersion) => string) | null;
  readonly activation: VersionedConfigActivation<TVersion>;
  /**
   * The stored value a `409` named, when the surface's refusal body carries one.
   * Omit it and the stored value waits for a `reload()` — see the module comment.
   */
  readonly storedFromConflict?: (
    error: WorkforceApiError,
  ) => Readonly<Record<string, string>> | null;
  /**
   * Is this read refusal really "there is nothing here yet"?
   *
   * Fix round 1, for U18. `empty` was reachable only from a `200` with zero versions,
   * but `GET /payroll/formulas` returns the single active formula and throws `404
   * WORKFORCE_NOT_FOUND` when a company has none
   * (`src/modules/payroll/routes.ts:246-250`, `src/modules/payroll/service.ts:481-483`).
   * That landed the hook in `retry`, where `ApiErrorNote` renders a `404` as "this
   * build does not serve that" — no retry, no create action. A company on its first
   * payroll run was shown a build error instead of the button the screen exists to
   * offer. Roadmap E0-4 moved U18's list to `GET /payroll/formulas/versions`, which
   * answers `200` with none, so no screen declares this today; it stays, tested, for a
   * single-resource read that refuses when there is nothing yet.
   *
   * Consulted **before** `retry`, and per surface on purpose: a `404` is an empty
   * state only where the backend says so, and treating every `404` as one globally is
   * the absence-as-zero mistake a level up.
   */
  readonly emptyOnRefusal?: (error: WorkforceApiError) => boolean;
}

/** One version, projected once here so the panel does not have to be generic. */
export interface VersionedConfigRow {
  readonly id: string;
  readonly label: string;
  readonly active: boolean;
  readonly fields: Readonly<Record<string, string>>;
  readonly precondition: VersionedConfigPrecondition | null;
  /**
   * The precondition in words, for the row and for a conflict note. `null` both for a
   * version that cannot be written and for one whose backend runs no check —
   * `precondition` is what tells those two apart.
   */
  readonly tokenLabel: string | null;
  /**
   * `precondition !== null`. A row that is not writable gets **no write control**:
   * `patch` and `activate` return before `submit` for it, so a control rendered there
   * would be present, enabled and inert — worse than absent or disabled. The panel
   * skips `rowAction` for such a row, so the rule is structural rather than
   * remembered four times.
   */
  readonly writable: boolean;
}

/** One touched field, against the active version. */
export interface VersionedConfigDiffEntry {
  readonly key: string;
  /** The active version's value, or `null` when it does not carry that field. */
  readonly from: string | null;
  readonly to: string;
}

/** A refused write, kept until the next write lands. */
export interface VersionedConfigConflict {
  readonly action: VersionedConfigAction;
  /** The refusal itself, so the panel can render the server's own sentence. */
  readonly error: WorkforceApiError;
  /** The version the write targeted, or `null` for a create. */
  readonly targetId: string | null;
  /** The newer token the server named, when it named one. */
  readonly storedToken: string | null;
  /** The stored value, when it is known. `null` means "not read back yet". */
  readonly stored: Readonly<Record<string, string>> | null;
  /** Set by `acknowledgeOverwrite`; cleared by the next conflict. */
  readonly acknowledged: boolean;
}

/** The conflict as held in state. `stored` is derived at render — see below. */
interface ConflictRecord {
  readonly action: VersionedConfigAction;
  readonly error: WorkforceApiError;
  readonly targetId: string | null;
  readonly storedToken: string | null;
  /** What the refusal body itself named, or `null`. */
  readonly storedFromError: Readonly<Record<string, string>> | null;
  /**
   * The list body in hand when the write was refused, by identity.
   *
   * Fix round 1. This used to be the **requested** read generation, and `reload()`
   * bumps that synchronously while `useApiQuery` keeps the previous `data` for the
   * whole round trip (it sets `loading` without clearing `data`) — so from the press
   * until the response landed, the pre-conflict row was printed under the "stored"
   * label, which is the exact lie this design says it refuses. Counting **responses**
   * instead of requests cannot be premature: a new body is a new object, and nothing
   * else advances it.
   */
  readonly dataAtRefusal: unknown;
  readonly acknowledged: boolean;
}

export interface VersionedConfigState<TVersion, TDraft> {
  readonly phase: VersionedConfigPhase;
  /** The read's own status, for a screen that needs finer control than `phase`. */
  readonly status: QueryStatus;
  readonly versions: readonly TVersion[];
  readonly rows: readonly VersionedConfigRow[];
  readonly active: TVersion | null;
  readonly activeRow: VersionedConfigRow | null;
  /** The read's refusal. Non-`null` in `retry`, and in `empty` via `emptyOnRefusal`. */
  readonly error: WorkforceApiError | null;
  /**
   * The `ETag` the read itself carried, unquoted, or `null`.
   *
   * The only place a precondition may come from on a single-resource surface
   * (`useApiQuery`'s own docstring, WP-L5): reconstructing it from the body would
   * mean a JavaScript `Date` and three lost digits. Surfaced here because
   * `preconditionOf` is a function of a row and cannot see it.
   */
  readonly etag: string | null;
  /** Re-reads the list. What `retry` is wired to, and what loads a stored value. */
  readonly reload: () => void;

  readonly draft: Partial<TDraft>;
  readonly touched: readonly string[];
  readonly setField: <K extends keyof TDraft & string>(key: K, value: TDraft[K]) => void;
  readonly clearField: (key: keyof TDraft & string) => void;
  readonly resetDraft: () => void;
  readonly diff: readonly VersionedConfigDiffEntry[];

  readonly conflict: VersionedConfigConflict | null;
  readonly acknowledgeOverwrite: (value: boolean) => void;
  /** Drops the conflict without acknowledging anything. `resetDraft` does it too. */
  readonly dismissConflict: () => void;
  /**
   * Forgets the last write's outcome — its refusal and its `saved`.
   *
   * Fix round 2. While nothing rendered `writeError` this was a shrug; now that the
   * panel does, a refusal note outlives the draft that caused it and there is no way
   * to take it off the screen. `resetDraft` and `dismissConflict` do this too, so a
   * screen that clears the form clears the note with it.
   */
  readonly dismissWrite: () => void;
  /** The last write's refusal, conflict or not. */
  readonly writeError: WorkforceApiError | null;
  readonly pending: boolean;
  /** The last write landed and nothing has been attempted since. */
  readonly saved: boolean;
  /**
   * May this write go out? False while any write is in flight, and false while an
   * unacknowledged conflict stands **on that same target**.
   *
   * Fix round 1: this was one boolean covering every write, so a conflict on one
   * version gated an unrelated `create` — and on U18, where every write was then a create,
   * a refused create gated the next one behind a checkbox reading "I still want to
   * overwrite their change", which described something that was not happening. The
   * gate exists to stop a blind re-apply against the row that moved, so it is scoped
   * to that row. Omit `target` for a create.
   */
  readonly canWrite: (target?: TVersion) => boolean;

  readonly create: () => void;
  readonly patch: (target: TVersion) => void;
  /** `target` is ignored — and may be omitted — when `activateIsCreate`. */
  readonly activate: (target?: TVersion) => void;
  /** This surface has a `PATCH`. Screens render no edit control when it does not. */
  readonly canPatch: boolean;
  /** Creating a version is the activation, so a screen can word its button. */
  readonly activateIsCreate: boolean;
}

const NO_VERSIONS: readonly never[] = [];

/** A draft value on screen. Never `[object Object]`, never a thrown formatter. */
function renderValue(value: unknown): string {
  if (value === undefined) return '';
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function tokenLabelOf(precondition: VersionedConfigPrecondition | null): string | null {
  if (precondition === null) return null;
  if (precondition.kind === 'if-match') return precondition.token;
  return precondition.kind === 'body' ? String(precondition.value) : null;
}

/**
 * The newer token a `409` named — and the four backends do not spell it alike.
 *
 * `details.current_etag` is the policies spelling
 * (`src/modules/policies/admin-service.ts:808,854,902,916`), which
 * `useApiMutation.ts`'s `currentEtag` reads for the same reason. **`actual` is the
 * alert-rules spelling** — the details there are `{ expected, actual }`,
 * `src/modules/alerting/service.ts:376-378` — and fix round 1 added it: without it
 * `conflict.storedToken` was always `null` on U16 and the panel printed "the server
 * did not name the stored version", which is the one thing this design promises to
 * say immediately. `expected` is deliberately **not** read: that is the caller's own
 * stale token coming back, not the winner's.
 *
 * Read so the refusal can **say** what the live token is, never so the client can
 * quietly retry with it: retrying under the conflict is the blind overwrite the
 * precondition exists to prevent.
 */
function storedTokenOf(error: WorkforceApiError): string | null {
  const { details } = error;
  const etag = details.current_etag;
  if (typeof etag === 'string') return etag;
  for (const key of ['actual', 'actual_version', 'current_version', 'version']) {
    const value = details[key];
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string') return value;
  }
  return null;
}

export function useVersionedConfig<TList, TVersion, TDraft extends object, TCreate>(
  surface: VersionedConfigSurface<TList, TVersion, TDraft, TCreate>,
): VersionedConfigState<TVersion, TDraft> {
  // The surface is rebuilt by the caller on every render, so it is read through a
  // ref inside the effects rather than depended on — depending on it would re-fire
  // the read on every keystroke anywhere on the screen. Updated in an effect rather
  // than during render: effects flush before any event handler can fire, so nothing
  // ever reads a stale surface and render stays free of side effects.
  const surfaceRef = useRef(surface);
  useEffect(() => {
    surfaceRef.current = surface;
  });

  const [readGeneration, setReadGeneration] = useState(0);
  const list = useApiQuery<TList>(surface.listPath, readGeneration);

  /**
   * The touched-fields draft, held as a plain record and handed out as
   * `Partial<TDraft>`.
   *
   * The state is untyped for one reason: a generic write — `next[key] = value` where
   * `key` ranges over `keyof TDraft` — is the one thing TypeScript cannot check, and
   * the workarounds are worse than one assertion at a boundary this file owns. Every
   * key that reaches here came from `setField`, whose signature **is** checked
   * against `TDraft`, so the record's shape is exactly what the assertion claims.
   *
   * `TDraft extends object` rather than `extends Record<string, unknown>` on purpose:
   * an `interface` does not satisfy the latter (no implicit index signature) and
   * `@typescript-eslint/consistent-type-definitions` requires interfaces repo-wide,
   * so the stricter constraint would have made every one of U15–U18 declare its
   * draft in the one shape the linter rejects.
   */
  const [draftState, setDraftState] = useState<Readonly<Record<string, unknown>>>({});
  const draft = draftState as unknown as Partial<TDraft>;
  const [conflict, setConflict] = useState<ConflictRecord | null>(null);
  const [lastAction, setLastAction] = useState<VersionedConfigAction | null>(null);
  /** Which version the last write targeted, so a refusal can be matched to a row. */
  const targetIdRef = useRef<string | null>(null);

  const activateMethod: 'POST' | 'PATCH' =
    surface.activation.kind === 'endpoint' ? (surface.activation.method ?? 'POST') : 'POST';

  // Three writes, three hooks, because `useApiMutation` binds its verb at the hook.
  // `patch` and `activate` pass a `pathOverride` on every press — their target is
  // chosen per press, not per mount — so the path bound here is only the default.
  const createCall = useApiMutation<TCreate, unknown>(surface.createPath, 'POST');
  const patchCall = useApiMutation<Readonly<Record<string, unknown>>, unknown>(
    surface.createPath,
    'PATCH',
  );
  const activateCall = useApiMutation<Readonly<Record<string, unknown>>, unknown>(
    surface.createPath,
    activateMethod,
  );

  /* ── The transition-to-`ok` side effects (T2-H) ─────────────────────────────
   *
   * Keyed on `status` alone, so each fires once per transition and a refusal —
   * which never reaches `'ok'` — fires none of them. A landed create or patch also
   * clears the draft: it is a version now. A landed activate does not, because the
   * draft was never part of that request.
   */
  useEffect(() => {
    if (createCall.status !== 'ok') return;
    setReadGeneration((value) => value + 1);
    setDraftState({});
    setConflict(null);
  }, [createCall.status]);

  useEffect(() => {
    if (patchCall.status !== 'ok') return;
    setReadGeneration((value) => value + 1);
    setDraftState({});
    setConflict(null);
  }, [patchCall.status]);

  useEffect(() => {
    if (activateCall.status !== 'ok') return;
    setReadGeneration((value) => value + 1);
    setConflict(null);
  }, [activateCall.status]);

  /* ── The refusals ──────────────────────────────────────────────────────────
   *
   * A compare-and-set refusal becomes the `conflict`; every other refusal stays an
   * ordinary `writeError` the screen renders through `ApiErrorNote`. Neither one
   * refetches, and neither one touches the draft.
   *
   * `error` is in each dependency list so two consecutive refusals are two
   * conflicts — the second one clearing the acknowledgement the first may have
   * been given is the whole point of the gate.
   */
  const noteRefusal = (action: VersionedConfigAction, error: WorkforceApiError | null): void => {
    if (error?.code !== VERSION_CONFLICT_CODE) return;
    setConflict({
      action,
      error,
      targetId: targetIdRef.current,
      storedToken: storedTokenOf(error),
      storedFromError: surfaceRef.current.storedFromConflict?.(error) ?? null,
      dataAtRefusal: list.data,
      acknowledged: false,
    });
  };

  useEffect(() => {
    if (createCall.status === 'error') noteRefusal('create', createCall.error);
  }, [createCall.status, createCall.error]);

  useEffect(() => {
    if (patchCall.status === 'error') noteRefusal('patch', patchCall.error);
  }, [patchCall.status, patchCall.error]);

  useEffect(() => {
    if (activateCall.status === 'error') noteRefusal('activate', activateCall.error);
  }, [activateCall.status, activateCall.error]);

  /* ── The projection ────────────────────────────────────────────────────────
   *
   * Recomputed every render rather than memoised. The inputs are the surface's own
   * functions, whose identity changes on every render of a caller that builds the
   * surface inline — a `useMemo` over them would either lie about its dependencies
   * or never hit, and the work here is a map over a handful of versions.
   */
  const versions: readonly TVersion[] =
    list.data === null ? NO_VERSIONS : surface.versions(list.data);

  const rows: readonly VersionedConfigRow[] = versions.map((version) => {
    const precondition = surface.preconditionOf(version);
    return {
      id: surface.idOf(version),
      label: surface.labelOf(version),
      active: surface.isActive(version),
      fields: surface.fieldsOf(version),
      precondition,
      tokenLabel: tokenLabelOf(precondition),
      writable: precondition !== null,
    };
  });

  const activeIndex = versions.findIndex((version) => surface.isActive(version));
  const active: TVersion | null = activeIndex === -1 ? null : (versions[activeIndex] ?? null);
  const activeRow: VersionedConfigRow | null =
    activeIndex === -1 ? null : (rows[activeIndex] ?? null);

  // A read refusal the surface classifies as "nothing here yet" is `empty`, with the
  // create action, not `retry` — U18's single-formula `404` was that, until roadmap E0-4
  // gave it a history. Everything else is `retry`.
  const refusedButEmpty =
    list.status === 'error' && list.error !== null && surface.emptyOnRefusal?.(list.error) === true;

  const phase: VersionedConfigPhase =
    list.status === 'loading'
      ? 'skeleton'
      : list.status === 'error'
        ? refusedButEmpty
          ? 'empty'
          : 'retry'
        : versions.length === 0
          ? 'empty'
          : 'ready';

  const touched = Object.keys(draft);
  const diff: readonly VersionedConfigDiffEntry[] = touched.map((key) => ({
    key,
    from: activeRow?.fields[key] ?? null,
    to: renderValue(draftState[key]),
  }));

  /**
   * The conflict, with the stored value filled in **only** once the list has been
   * read back. Before that the row still holds the value the person read, and
   * printing that under a "stored" label would say the opposite of what happened.
   */
  const conflictView: VersionedConfigConflict | null =
    conflict === null
      ? null
      : {
          action: conflict.action,
          error: conflict.error,
          targetId: conflict.targetId,
          storedToken: conflict.storedToken,
          acknowledged: conflict.acknowledged,
          stored:
            conflict.storedFromError ??
            (list.data === conflict.dataAtRefusal
              ? null
              : (rows.find((row) => row.id === conflict.targetId)?.fields ?? null)),
        };

  const activeCall =
    lastAction === 'patch' ? patchCall : lastAction === 'activate' ? activateCall : createCall;
  const pending =
    createCall.status === 'pending' ||
    patchCall.status === 'pending' ||
    activateCall.status === 'pending';
  /**
   * A standing conflict gates the target it happened on, and only that one.
   *
   * `targetId` is `null` for a create on both sides, so a refused create gates the
   * next create and nothing else, and a conflict on one version leaves an unrelated
   * create alone.
   */
  const blockedBy = (targetId: string | null): boolean =>
    conflict !== null && !conflict.acknowledged && conflict.targetId === targetId;

  const canWrite = (target?: TVersion): boolean => {
    if (pending) return false;
    return !blockedBy(target === undefined ? null : surface.idOf(target));
  };

  /* ── The actions ───────────────────────────────────────────────────────────
   *
   * Plain functions, not `useCallback`: they are event handlers, they close over
   * this render's draft and gates, and a memoised identity would only buy a stale
   * closure to debug later.
   */
  const reload = (): void => {
    setReadGeneration((value) => value + 1);
  };

  const create = (): void => {
    if (!canWrite()) return;
    targetIdRef.current = null;
    setLastAction('create');
    void createCall.submit(surface.createBody(draft));
  };

  const patch = (target: TVersion): void => {
    const { patchPath } = surface;
    if (patchPath === null || !canWrite(target)) return;
    // No precondition is no write. A blind overwrite is the failure the whole CAS
    // shape exists to prevent, so it is refused here as well as hidden by the
    // screen — see the module comment on `preconditionOf`.
    const precondition = surface.preconditionOf(target);
    if (precondition === null) return;
    targetIdRef.current = surface.idOf(target);
    setLastAction('patch');
    void patchCall.submit(
      bodyWith(draftState, precondition),
      patchPath(target),
      headersFor(precondition),
    );
  };

  const activate = (target?: TVersion): void => {
    const { activation } = surface;
    // The alias. One `POST` to `createPath` and nothing else, so a create-is-activate
    // surface calls the same `activate` as every other screen instead of special-casing
    // itself (U18 did, until roadmap E0-4).
    if (activation.kind === 'create') {
      create();
      return;
    }
    if (target === undefined || !canWrite(target)) return;
    const precondition = surface.preconditionOf(target);
    if (precondition === null) return;
    targetIdRef.current = surface.idOf(target);
    setLastAction('activate');
    void activateCall.submit(
      bodyWith(activation.body?.(target) ?? {}, precondition),
      activation.path(target),
      headersFor(precondition),
    );
  };

  const setField = <K extends keyof TDraft & string>(key: K, value: TDraft[K]): void => {
    setDraftState((current) => ({ ...current, [key]: value }));
  };

  const clearField = (key: keyof TDraft & string): void => {
    setDraftState((current) => {
      if (!(key in current)) return current;
      // Destructured out rather than `delete`d: a computed `delete` is banned
      // repo-wide (`@typescript-eslint/no-dynamic-delete`), and the rest object is a
      // new identity, which is what React needs to re-render anyway.
      const { [key]: _dropped, ...rest } = current;
      return rest;
    });
  };

  return {
    phase,
    status: list.status,
    versions,
    rows,
    active,
    activeRow,
    error: list.error,
    etag: list.etag,
    reload,

    draft,
    touched,
    setField,
    clearField,
    resetDraft: () => {
      setDraftState({});
      // The conflict goes with it. It described a draft that no longer exists, and
      // leaving it standing would gate the next write behind an acknowledgement of an
      // overwrite nobody is about to make. So does the last write's outcome, for the
      // same reason one level along: the panel renders that refusal, and a note about
      // a draft that has been thrown away is a note about nothing.
      setConflict(null);
      setLastAction(null);
    },
    diff,

    conflict: conflictView,
    acknowledgeOverwrite: (value) => {
      setConflict((current) => (current === null ? null : { ...current, acknowledged: value }));
    },
    dismissConflict: () => {
      setConflict(null);
      // A version conflict is the `writeError` as well, so dismissing it here and
      // leaving the note behind would take away the rich rendering and keep the poor
      // one.
      setLastAction(null);
    },
    dismissWrite: () => {
      setLastAction(null);
    },
    writeError: lastAction === null ? null : activeCall.error,
    pending,
    saved: lastAction !== null && activeCall.status === 'ok',
    canWrite,

    create,
    patch,
    activate,
    canPatch: surface.patchPath !== null,
    activateIsCreate: surface.activation.kind === 'create',
  };
}

/** The request body: the touched draft, plus the precondition when it rides there. */
function bodyWith(
  draft: Readonly<Record<string, unknown>>,
  precondition: VersionedConfigPrecondition,
): Readonly<Record<string, unknown>> {
  if (precondition.kind !== 'body') return { ...draft };
  return { ...draft, [precondition.field]: precondition.value };
}

/**
 * The request headers. Quoted, because `If-Match` is an entity-tag header and the
 * server strips the quoting; the value between the quotes is the read's own token,
 * untouched.
 */
function headersFor(precondition: VersionedConfigPrecondition): Readonly<Record<string, string>> {
  return precondition.kind === 'if-match' ? { 'If-Match': `"${precondition.token}"` } : {};
}
